/**
 * Node customizado para Template WhatsApp Oficial (HSM)
 * Exibe preview do template selecionado com variáveis configuradas
 * Inclui toolbar flutuante ao selecionar
 * 
 * RELEASE: 1.0.0 - 2026-01-31 - Novo node de template
 */

import { Handle, Position, NodeProps } from '@xyflow/react';
import { FileText, MessageSquare, Inbox } from 'lucide-react';
import { FlowNodeData, MessageTemplateConfig } from '@/types/flows';
import { FlowNodeToolbar } from './FlowNodeToolbar';
import { Badge } from '@/components/ui/badge';

/**
 * Labels em português para categorias de template
 */
const categoryLabels: Record<string, string> = {
  UTILITY: 'Utilitário',
  MARKETING: 'Marketing',
  AUTHENTICATION: 'Autenticação',
};

/**
 * Cores por categoria de template
 */
const categoryColors: Record<string, string> = {
  MARKETING: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
  UTILITY: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  AUTHENTICATION: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
};

/**
 * Componente visual do node de template WhatsApp
 */
export function TemplateNode({ data, id, selected }: NodeProps) {
  const nodeData = data as FlowNodeData;
  const config = nodeData.config as MessageTemplateConfig | undefined;
  
  // Callbacks injetados via FlowCanvas
  const onEdit = (nodeData as any).onEdit;
  const onDuplicate = (nodeData as any).onDuplicate;
  const onDelete = (nodeData as any).onDelete;
  const onTestFromHere = (nodeData as any).onTestFromHere;
  
  // Dados do template
  const templateName = config?.template_name;
  const templateCategory = config?.template_category || 'UTILITY';
  const templateLanguage = config?.template_language || 'pt_BR';
  const inboxName = config?.inbox_name;
  const bodyText = config?.template_body_text;
  
  // Preview do texto (truncado)
  const preview = bodyText && bodyText.length > 80 
    ? bodyText.substring(0, 80) + '...' 
    : bodyText;
  
  // Verifica se está configurado
  const isConfigured = !!templateName && !!config?.inbox_id;

  return (
    <div className="bg-card rounded-lg shadow-lg border-2 border-border min-w-[220px] max-w-[300px]">
      {/* Toolbar flutuante */}
      <FlowNodeToolbar
        nodeId={id}
        nodeType={nodeData.type}
        onEdit={onEdit}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onTestFromHere={onTestFromHere}
        isVisible={selected}
      />
      
      <Handle
        type="target"
        position={Position.Top}
        className="!bg-border !w-3 !h-3"
      />
      
      <div className="p-3">
        {/* Header do node */}
        <div className="flex items-center gap-2 mb-2">
          <div className="bg-emerald-500 p-2 rounded text-white">
            <FileText className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-sm text-foreground">
              {nodeData.config?.nickname || 'Enviar Template'}
            </div>
            {templateName && (
              <div className="text-xs text-muted-foreground truncate">
                {templateName}
              </div>
            )}
          </div>
        </div>
        
        {/* Conteúdo configurado */}
        {isConfigured ? (
          <>
            {/* Preview do body */}
            {preview && (
              <div className="text-xs text-muted-foreground bg-muted/50 rounded p-2 mt-2 mb-2">
                <MessageSquare className="h-3 w-3 inline mr-1" />
                {preview}
              </div>
            )}
            
            {/* Badges de info */}
            <div className="flex flex-wrap gap-1.5 mt-2">
              <Badge 
                variant="outline" 
                className={`text-[10px] px-1.5 py-0.5 ${categoryColors[templateCategory] || categoryColors.UTILITY}`}
              >
                {categoryLabels[templateCategory] || templateCategory}
              </Badge>
              <Badge variant="outline" className="text-[10px] px-1.5 py-0.5">
                {templateLanguage}
              </Badge>
            </div>
            
            {/* Inbox selecionada */}
            {inboxName && (
              <div className="flex items-center gap-1 mt-2 text-[10px] text-muted-foreground">
                <Inbox className="h-3 w-3" />
                <span className="truncate">{inboxName}</span>
              </div>
            )}
          </>
        ) : (
          /* Estado não configurado */
          <div className="text-xs text-muted-foreground italic bg-muted/30 rounded p-2 mt-2">
            Clique para configurar template
          </div>
        )}
      </div>
      
      <Handle
        type="source"
        position={Position.Bottom}
        className="!bg-border !w-3 !h-3"
      />
    </div>
  );
}
